import { useEffect, useRef, useState, useCallback } from 'react';

import { useWellSelectionStore } from '@cores/stores/useWellSelectionStore';
import { useAuthBiogems } from '@cores/security/hooks/useAuthBiogems';
import { LOG_MODULES,APP_CONFIG,SERVICE_KEYS,ALL_RECORDS,NONE_RECORDS } from '@config/config';
import { log } from '@libs/utils/helper.js';

import { buildSqlExpression, buildCheckedIdsClause, combineSqlClauses } from '../utils/sqlUtils';

import { zoomToWells, syncTableHighlights, zoomToReservation } from './helpers/utils';
import { createFeatureTable } from './helpers/tableFactory';
import { bindAllEvents } from './helpers/eventOrchestrator';
import { bindMenuConfig } from './helpers/bindMenuConfig';

const ftTblDebug=LOG_MODULES.FEATURETABLBE;
const zoomForDetail=APP_CONFIG.MAPS.Normal.zoomForDetail;
const CHECKED_ID_FIELD = APP_CONFIG.MAPS.Well.queryField; // ← same field used by MapSelection's rectangle query

const useWellFeatureTable = ({ mapView, layerTitle, ftrTblId, setFeaturesOpen, filterChecked }) => {  
  const tableRef                = useRef(null);
  const highlightLayerRef       = useRef(null);
  const isMapExtentFilterEnabledRef = useRef(false);
  const [isReady, setIsReady]   = useState(false);
  const handlersRef = useRef({});

  // Remembers the last search submitted — { queryParams, pageSize, isPagination } | null.
  // Needed because applyDefinitionExpression is also triggered by the
  // checkedRds/filterChecked resync effect below, which has no fresh
  // activeParams of its own and must fall back to whatever was last submitted.
  const lastSearchRef = useRef(null);

  // ── Store (direct) ───────────────────────────────────────────────────────
  const checkedRds       = useWellSelectionStore((s) => s.checkedRds);//  console.error("useWellFeatureTable checkedRds",checkedRds);
  const setCheckedRds    = useWellSelectionStore((s) => s.setCheckedRds);
  const selectedApiInfo  = useWellSelectionStore((s) => s.selectedApiInfo);
  const setSelectedApiInfo = useWellSelectionStore((s) => s.setSelectedApiInfo);
  const defExpress   = useWellSelectionStore((s) => s.defExpress); // ← here

  //const {user} = useAuthBiogems();//  console.log("User in PgAppHome:", user.role);
  //log(ftTblDebug, "useWellFeatureTable checkedRds selectedApiInfo",checkedRds, selectedApiInfo);

  // Single source of truth for "are there checked wells right now" — used
  // both to seed isCheckedMode and to decide filterBySelectionEnabled at
  // table creation, so the two can never disagree.
  const hasCheckedRds = (checkedRds?.length ?? 0) > 0;

  // Mirrors table.filterBySelectionEnabled in React state, so UI (e.g. the
  // search box) can react to it. Initialized lazily to match the exact same
  // condition used at table creation below — by design, if the user reloads
  // with wells already checked (persisted store), we start in Checked Mode
  // to restore where they left off.
  const [isCheckedMode, setIsCheckedMode] = useState(() => hasCheckedRds);

  // Stable ref for ESRI event callbacks

  useEffect(() => {
    handlersRef.current = {
      onSingleClick: setCheckedRds,
      onDoubleClick: setSelectedApiInfo,
      checkedRds,
      selectedApiInfo,
      onCloseFeaturesPanel:() => setFeaturesOpen(false),
    };
  }, [checkedRds, selectedApiInfo, setCheckedRds, setSelectedApiInfo]);
  // ── Reactive Menu Update ──────────────────────────────────────────────────
  useEffect(() => {
  // Only run if the table is initialized and ready
 if (!isReady || !tableRef.current) return;
    
    // We pass the current state to the menu binder
    bindMenuConfig({
      table: tableRef.current,
      mapView,
      highlightLayer: highlightLayerRef.current,
      handlersRef, // This contains the latest checkedRds/selectedApiInfo from your other effect
      isMapExtentFilterEnabledRef,
      onModeChange: setIsCheckedMode, // ← keep React state in sync when menu toggles mode
    });
    //log(ftTblDebug, "Menu configuration refreshed due to state change.");
}, [isReady, checkedRds, selectedApiInfo, mapView]);
  // ── Init table ───────────────────────────────────────────────────────────
  useEffect(() => {
    let isMounted = true;
    let cleanupEvents = null;
    if (!mapView?.map || !ftrTblId) return;

    const init = async () => {
      try {
        const { table, highlightLayer, error } = await createFeatureTable(
          mapView, layerTitle, ftrTblId,
          { filterBySelectionEnabled: hasCheckedRds }
        );
        if (!isMounted) { table?.destroy(); return; }
        if (error) return;

        tableRef.current        = table;
        highlightLayerRef.current = highlightLayer;

        cleanupEvents = bindAllEvents({
          table, mapView, highlightLayer, handlersRef, isMapExtentFilterEnabledRef,
        });
        setIsReady(true);
      } catch (err) {
        console.error('[useWellFeatureTable] Init Error:', err);
      }
    };

    init();
    return () => {
      isMounted = false;
      setIsReady(false);
      cleanupEvents?.();
      tableRef.current?.destroy();
      tableRef.current = null;
    };
  }, [mapView, layerTitle, ftrTblId]);

  // ── Sync highlights when store changes ───────────────────────────────────
  useEffect(() => {
    if (isReady && tableRef.current) {
      syncTableHighlights(tableRef.current, checkedRds ?? [])
        .catch((err) => console.error('Sync error', err));
    }
  }, [isReady, checkedRds]);

  // ── Initial zoom ─────────────────────────────────────────────────────────
  useEffect(() => {
      if (!isReady || !tableRef.current || !mapView) return;

      const layer  = tableRef.current.layer;
      const hLayer = highlightLayerRef.current;

      if (selectedApiInfo?.id) {
        //console.error("77777 useWellFeatureTable:selectedApiInfo", selectedApiInfo?.id);
        zoomToWells(mapView, layer, selectedApiInfo.id, true, hLayer, zoomForDetail);
      } else if (checkedRds?.length > 0) {
        //console.error("88888 useWellFeatureTable:checkedRds", checkedRds);
        zoomToWells(mapView, layer, checkedRds, false, undefined, zoomForDetail);
      } else {
        // ✅ Keep this new fallback logic
        const reservationWhere = defExpress[SERVICE_KEYS.NIOGEMS_MAP_SERVICES]
        //console.error("99999 useWellFeatureTable",reservationWhere);
        if (reservationWhere && reservationWhere !== ALL_RECORDS && reservationWhere !== NONE_RECORDS) {
          log(ftTblDebug, "Initial load: Zooming to reservation extent");
          zoomToReservation(mapView, reservationWhere);
        } else {
          log(ftTblDebug, "Initial load: No reservation — zooming to app default");
          mapView.goTo({
            center: APP_CONFIG.MAPS.Well.center,
            zoom:   APP_CONFIG.MAPS.Well.appZoom,
          });
        }
      }
  }, [isReady, mapView]); // ✅ Tight deps — runs once naturally

  // ── Filter/search ────────────────────────────────────────────────────────

  // Builds the combined SQL (search AND, optionally, checked-ids) and
  // pushes it onto the table. Single source of truth so both the
  // "user submitted a search" path and the "checkedRds/filterChecked
  // changed" path stay identical.
  //
  // `params` lets a caller with fresh data (handleFeatureTableSearch) pass
  // it directly. When omitted — e.g. the resync effect, which has no new
  // search of its own — we fall back to the last search that was submitted.
  const applyDefinitionExpression = useCallback((params) => {
    if (!tableRef.current) return;

    const { queryParams, pageSize, isPagination } = params ?? lastSearchRef.current ?? {};

    const baseSql = buildSqlExpression(queryParams ?? undefined);
    const checkedClause = filterChecked ? buildCheckedIdsClause(CHECKED_ID_FIELD, checkedRds) : null;

    const finalSql = combineSqlClauses(baseSql, checkedClause);

    // 1. Apply the new SQL filter
    tableRef.current.definitionExpression = finalSql;
    // 2. Safely apply pagination status
    tableRef.current.paginationEnabled = isPagination ?? true;
    // 3. 🌟 Apply size and reset index if paginating
    if (tableRef.current.paginationEnabled && pageSize != null) {
      tableRef.current.pageSize = pageSize;
      tableRef.current.pageIndex = 0; // Prevent out-of-bounds blank page bugs!
    }
    // 4. Refresh the table to fetch and draw the new data
    tableRef.current.refresh();

  }, [filterChecked, checkedRds]);

  const handleFeatureTableSearch = useCallback((activeParams) => {
    log(LOG_MODULES.WELLSEARCH, "[useWellFeatureTable] handleFeatureTableSearch:", activeParams);

    const { pageSize = null, isPagination = true, queryParams = null } = activeParams ?? {};

    // 🌟 Remember this search so the resync effect can rebuild later
    // without needing fresh activeParams of its own.
    lastSearchRef.current = { queryParams, pageSize, isPagination };

    applyDefinitionExpression(lastSearchRef.current);
  }, [applyDefinitionExpression]);

  // Re-apply the combined expression whenever checkedRds or filterChecked
  // change AFTER a search has already been submitted at least once — e.g.
  // the user rectangle-selects more wells while "filter checked points"
  // is on, or toggles the checkbox on/off. No fresh params exist here, so
  // applyDefinitionExpression falls back to lastSearchRef.current.
  useEffect(() => {
    if (!isReady || !tableRef.current) return;
    if (lastSearchRef.current === null) return; // no search applied yet — nothing to resync
    applyDefinitionExpression();
  }, [isReady, filterChecked, checkedRds, applyDefinitionExpression]);

  return { tableRef, isReady, handleFeatureTableSearch, isCheckedMode };
};

export default useWellFeatureTable;